import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { formatDataGeneral, getFileLinkIfExist } from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { filterRecordsByDpatYearAttribute, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildLedActivityDocLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const documentTypes = [
    { type: "Annual Action Plan", title: "Annual Action Plan" },
    { type: "LED Activities Report", title: "LED Activity Report" },
  ];

  return documentTypes.map(({ type, title }) => {
    const matchedDocs = formatDataGeneral(
      documentData,
      "DPAT | M&E Tool Type",
      type,
    ) || [];

    let reportLink = "";
    if (matchedDocs.length > 0) {
      reportLink = getFileLinkIfExist(
        documentReports,
        DOCUMENT_HUB_FILE_ELEMENT,
        matchedDocs[0]?.trackedEntity,
      );
    }

    return {
      title,
      link: reportLink
        ? fileUrl(reportLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    };
  });
}

const DistrictLEDActivityPlan = forwardRef(({
  year,
  districtId,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [data, setData] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [scorei, setScorei] = useState(0);
  const [maxScore, setMaxScore] = useState(2);

  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  const liveDocumentLinks = useMemo(
    () => buildLedActivityDocLinks(documents),
    [documents],
  );

  const displayDocumentLinks = isAssessmentStarted && savedAssessmentData?.links?.length
    ? savedAssessmentData.links
    : documentLinks.length
      ? documentLinks
      : liveDocumentLinks;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI6",
      area: "Local Economic Development (LED)",
      maxScore,
      data,
      links: displayDocumentLinks,
      scorei
    }),
  }));

  const DPAT_SLUG = "sdi6.0-6.1";

  const calculatePercentage = (total, value) => {
    const totalNum = parseFloat(total);
    const valueNum = parseFloat(value);

    if (isNaN(totalNum) || isNaN(valueNum) || totalNum === 0) {
      return 0;
    }

    return ((valueNum / totalNum) * 100).toFixed(2);
  };

  const loadFromDhis2 = useCallback(() => {
    setDocumentLinks(liveDocumentLinks);

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=ArLnAxhykoz&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=1000`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=ArLnAxhykoz&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=10000`)
            .then(resp => {
              const aap = isDpatScoreRelaxationExempt()
                ? filterRecordsByDpatYearAttribute(result.data.instances, year)
                : (formatDataGeneral(result.data.instances, "Year", `${year}`) || []);
              const reports = resp.data.instances;
              const aapLedPlans = formatDataGeneral(aap, "Activity Type", "LED Activity") || [];
              const aapTotal = aap.length;
              const aapLED = aapLedPlans.length;
              let aapLEDImplemented = 0

              aapLedPlans.forEach(plan => {
                const currentReport = reports.find(rep => rep.trackedEntity === plan.trackedEntity);

                if (currentReport) {
                  currentReport.dataValues.forEach(rep => {
                    if (rep.dataElement === "SZcHb5mvjJx" && rep.value === "Completed") {
                      aapLEDImplemented++;
                    }
                  })
                }
              });

              const percentage = calculatePercentage(aapLED, aapLEDImplemented);

              const temp = {
                aapTotal,
                aapLED,
                aapLEDImplemented,
                percentage
              }

              setData([temp]);

              if (percentage >= 90 && aapLED > 0) {
                setScorei(scoreOnlyIfDocumentLinksComplete(liveDocumentLinks, 2));
              } else {
                setScorei(0);
              }


            })
            .catch(err => console.log(err))
        }
      })
      .catch(err => console.log(err))
  }, [districtId, year, liveDocumentLinks]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.data) setData(snap.data);
      if (snap.links) setDocumentLinks(snap.links);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, documents, loadFromDhis2],
  });

  const dataColumn = [
    {
      title: "No. of Activities in AAP",
      dataIndex: "aapTotal",
      key: "aapTotal"
    },
    {
      title: "No. of LED activities in AAP",
      dataIndex: "aapLED",
      key: "aapLED"
    },
    {
      title: "No. of LED activities in AAP implemented",
      dataIndex: "aapLEDImplemented",
      key: "aapLEDImplemented"
    },
    {
      title: "% Implementation of LED activities",
      dataIndex: "percentage",
      key: "percentage"
    }
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi6.0-6.1-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 6.0-6.1 Actual Score:" score={scorei} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Implementation of LED activities</Title>
          <Table
            columns={dataColumn}
            dataSource={data}
            pagination={false} bordered />

          <Title level={5} style={{ marginTop: "20px" }}>Evidence of attached documents (Document Hub)</Title>
          <Table
            columns={documentTableColumns}
            dataSource={displayDocumentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default DistrictLEDActivityPlan;